import React, {useState, useEffect, useCallback, useMemo} from 'react';
import {Container, Card, Accordion, Row, Col, Form, InputGroup } from 'react-bootstrap';

import Stack from '../../../../../../components/common/Stack';
import {fromCents, normalizeCurrency, remainingTransactionAmount, toCents} from '../../../refundUtils';
import styles from '../../Wizard.module.scss';

export const Item = (props) => {
    const {itemUpdate} = props;
    
    const [accordionToggle, setAccordionToggle] = useState(false);
    const [totalRefund, setTotalRefund] = useState(0);
    const maxRefund = useMemo(() => remainingTransactionAmount(props.transaction), [props.transaction]);
    const [refundAmount, setRefundAmount] = useState(
        props?.data?.refund_type === "custom amount" ? normalizeCurrency(props.data.refund_amount) : ""
    );
    const [checked, setChecked] = useState({
        selected: true,
        full: !props?.data?.refund_type ? true : props?.data?.refund_type === "full amount",
        custom: props?.data?.refund_type === "custom amount" || false
    });

    const getTotalRefund = useCallback(()=>{
        let _refund = 0;
        if (checked.full) _refund = +maxRefund;
        else if (checked.custom) _refund = +refundAmount;

        _refund = normalizeCurrency(_refund);
        setTotalRefund(_refund.toFixed(2));
        itemUpdate({transaction: props.transaction, checked: checked, refund: _refund});
    },[checked, refundAmount, itemUpdate, props.transaction, maxRefund]);

    const toggleCheckHandler = useCallback((e)=>{
        e.preventDefault();
        e.stopPropagation();
        
        setChecked(prev => {
            let _checked = !prev.selected;

            if (!_checked) {
                setRefundAmount("");
                return {selected: _checked, full: false, custom: false};
            } else {
                return {selected: _checked, full: true, custom: false};
            }
        });
    }, []);

    const checkHandler = useCallback(e => {
        const _values = {selected: true, full: false, custom: false};
        _values[e.target.name] = e.target.checked;
        if (!_values.full && !_values.custom) _values.selected = false;
        if (e.target.name !== "custom") setRefundAmount("");
        setChecked(prev => ({...prev, ..._values}));
    }, []);

    const checkTotal = useCallback(e => {
        if (e.target.value === "") {
            checkHandler({target: {name: "custom", checked: false}});
            setRefundAmount("");
            return;
        }

        const amountInCents = Math.min(toCents(maxRefund), Math.max(0, toCents(e.target.value)));
        const amount = fromCents(amountInCents);
        checkHandler({target: {name: "custom", checked: amountInCents > 0}});
        setRefundAmount(amount);
    }, [checkHandler, maxRefund]);

    useEffect(()=>{
        //make sure we have a maxrefund before we refund anything
        if(maxRefund > 0)getTotalRefund();
    },[maxRefund, getTotalRefund, checked]);

    if (!props.transaction) return null;


    const transaction_name = `${
        props.transaction.transaction_payment_method_id === 1 ?
            `
            ${props.transaction?.cc_type || "Card"} 
            ${props.transaction?.cc_number ? ` ending in ${props.transaction.cc_number}` : ""}
            `
        :
            `
            ${props.transaction?.transaction_payment_method_name || "Payment"}
            ${props.transaction.transaction_response?.check_number ? ` #${props.transaction.transaction_response.check_number }` : ""}
            `
    }`;

    return (
        <>
            <Accordion.Toggle id={"refund-accordion-toggle"} as={Card.Header} eventKey={props.index} onClick={e=>{setAccordionToggle(!accordionToggle)}}>
                <Row className={`${styles.item} ${accordionToggle ? styles.active : ''}`}>
                    <Col sm={12} lg={9} className="py-2 position-relative">
                        <i className={`${styles["item-check"]} far fa-${checked?.selected ? "check-square" : "square"}`} onClick={toggleCheckHandler} />
                        <Stack direction="horizontal" spacing={2}>
                            <i className={`ms-5 me-4 far fa-chevron-${accordionToggle ? "up" : "down"}`}/>
                            <div> 
                                <span className="bold">{transaction_name}</span><br/>
                                <span className="small">Max Refund: ${maxRefund.toFixed(2)}</span>
                            </div>
                        </Stack>
                    </Col>
                    <Col sm={12} lg={3} className="py-2 text-right">
                        Refund for this Payment:<br/>
                        <span className="bold">${totalRefund}</span>
                    </Col>
                </Row>
            </Accordion.Toggle>
            <Accordion.Collapse eventKey={props.index}>
                <Container className="mt-2">
                    <Row className="ms-3">
                        <Col sm={12} lg={4}>
                            <div className={styles["info-pair"]}>
                                <span>
                                    <Form.Check 
                                        id="refund-full"
                                        name="full"
                                        type="checkbox" 
                                        value={+maxRefund}
                                        checked={checked.full}
                                        onChange={checkHandler}
                                        className="ps-0"
                                    /> 
                                    <label htmlFor="refund-full">Total Amount:</label>
                                </span>
                                <span className="text-right">${maxRefund.toFixed(2)}</span>
                            </div>
                            <Form.Group controlId="refund-custom-input" className={`${styles["colspan"]}`}>
                                <Form.Label>Custom Refund Amount</Form.Label>
                                <InputGroup>
                                    <InputGroup.Prepend>
                                        <InputGroup.Text>$</InputGroup.Text>
                                    </InputGroup.Prepend>
                                    <Form.Control 
                                        type="number" 
                                        name="refund-custom-input" 
                                        min={0}
                                        max={+maxRefund}
                                        step={0.01}
                                        value={refundAmount} 
                                        onChange={checkTotal}
                                    />
                                </InputGroup>
                            </Form.Group>
                        </Col>
                    </Row>
                    <hr/>
                </Container>
            </Accordion.Collapse>
        </>
    );
}
